import React, { useState } from 'react';
import { FaUserPlus } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import api, { errorMessage, saveSession } from '../api';
import { inputClass, Field, Button, Alert } from './attendance/ui';

/**
 * Self-service account request. Privileged roles are never granted here — the
 * API downgrades anything above supervisor, and HR promotes staff afterwards.
 */
export default function RegisterPage() {
  const [form, setForm] = useState({
    name: '', username: '', email: '', phone: '', position: '',
    password: '', confirmPassword: '', role: 'employee',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const update = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const handleRegister = async (event) => {
    event.preventDefault();
    if (form.password !== form.confirmPassword) {
      setError('The passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Choose a password of at least 8 characters.');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const { data } = await api.post('auth/register/', {
        name: form.name || form.username,
        username: form.username,
        email: form.email,
        phone: form.phone,
        position: form.position,
        password: form.password,
        role: form.role,
      });
      saveSession(data);
      navigate('/dashboard');
    } catch (err) {
      setError(errorMessage(err, 'Registration failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-10 px-4">
      <div className="w-full max-w-lg p-8 bg-white rounded-2xl shadow-2xl">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-[#15A857] rounded-2xl flex items-center justify-center mb-4">
            <FaUserPlus className="text-white text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Request an account</h2>
          <p className="text-gray-500 text-sm mt-1">
            Your department and location are assigned by HR after your account is created.
          </p>
        </div>

        <form onSubmit={handleRegister} className="mt-8 space-y-4">
          <Alert>{error}</Alert>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name">
              <input required value={form.name} onChange={update('name')}
                className={inputClass} placeholder="Abebe Kebede" />
            </Field>
            <Field label="Username">
              <input required value={form.username} onChange={update('username')}
                className={inputClass} placeholder="abebe.kebede" />
            </Field>
            <Field label="Email">
              <input required type="email" value={form.email} onChange={update('email')}
                className={inputClass} placeholder="you@college.edu.et" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={update('phone')}
                className={inputClass} placeholder="+251 9…" />
            </Field>
            <Field label="Position">
              <input value={form.position} onChange={update('position')}
                className={inputClass} placeholder="Mathematics Teacher" />
            </Field>
            <Field label="Role">
              <select value={form.role} onChange={update('role')} className={inputClass}>
                <option value="employee">Employee / Staff</option>
                <option value="supervisor">Supervisor</option>
              </select>
            </Field>
            <Field label="Password" hint="At least 8 characters.">
              <input required type="password" minLength="8" value={form.password}
                onChange={update('password')} className={inputClass} />
            </Field>
            <Field label="Confirm password">
              <input required type="password" value={form.confirmPassword}
                onChange={update('confirmPassword')} className={inputClass} />
            </Field>
          </div>

          <Button type="submit" disabled={isLoading} className="w-full py-3">
            {isLoading ? 'Creating account…' : 'Create account'}
          </Button>
        </form>

        <p className="mt-6 text-center text-gray-600 text-sm">
          Already have an account?{' '}
          <Link to="/" className="text-[#15A857] hover:text-green-700 font-semibold">
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
}
